import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ordersClient, type Order } from '@/lib/orders-client';
import { conversationClient } from '@/lib/gupshup-client';
import { statusMessageOffer } from './status-message-offer';

interface ConfirmOrderDialogProps {
    order: Order | null;
    onConfirm: (orderId: string) => void;
    onCancel: () => void;
}

export function ConfirmOrderDialog({ order, onConfirm, onCancel }: ConfirmOrderDialogProps) {
    const { t } = useTranslation('orders');
    const [message, setMessage] = useState('');
    // §7 / D8: never send by default. The operator opts in per confirmation — see statusMessageOffer.
    const [sendMessage, setSendMessage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const phone = order?.customer?.phone;
    // Whether a status-change WhatsApp may even be OFFERED here. A web / external-agent order's
    // status surface is its tracking page, so the composer is not rendered at all for one.
    const canOfferMessage = statusMessageOffer({ channel: order?.channel, phone });

    useEffect(() => {
        if (order) {
            const defaultMessage =
                order.deliveryType === 'delivery'
                    ? t('confirmDialog.defaultMessageDelivery', {
                          minutes: order.estimatedDeliveryMinutes ?? 30,
                      })
                    : t('confirmDialog.defaultMessagePickup');
            setMessage(defaultMessage);
            setError(null);
            // A fresh order re-arms the opt-in as "off": the tick belongs to one confirmation, and
            // carrying it over would be the pre-ticked default D8 removed, one dialog later.
            setSendMessage(false);
        }
    }, [order, t]);

    const willSend = canOfferMessage && sendMessage;

    const handleConfirm = async () => {
        if (!order) return;
        setLoading(true);
        setError(null);

        try {
            await ordersClient.updateStatus(order.id, 'confirmed');

            if (willSend && phone && message.trim()) {
                try {
                    await conversationClient.messages.sendText({
                        to: phone,
                        body: message.trim(),
                        role: 'assistant',
                        conversationId: order.conversationId,
                    });
                } catch (msgErr) {
                    console.error('Failed to send WhatsApp message:', msgErr);
                    toast.error(t('confirmDialog.confirmedMessageFailed'));
                    onConfirm(order.id);
                    return;
                }
            }

            toast.success(willSend ? t('confirmDialog.confirmed') : t('confirmDialog.confirmedNoMessage'));
            onConfirm(order.id);
        } catch (err) {
            console.error('Failed to confirm order:', err);
            setError(t('confirmDialog.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={!!order} onOpenChange={(open) => !open && onCancel()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('confirmDialog.title')}</DialogTitle>
                    <DialogDescription>
                        {canOfferMessage ? t('confirmDialog.description') : t('confirmDialog.descriptionStatusOnly')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    {/* The message block exists only for a WhatsApp order with a phone. A web or
                        external-agent order gets exactly one placement confirmation (§7) and the
                        tracking page after that, so there is nothing here to opt into. */}
                    {canOfferMessage ? (
                        <>
                            <div className="rounded-lg border border-border p-3 space-y-3">
                                <label htmlFor="confirm-order-send-message" className="flex cursor-pointer items-start gap-2.5 text-sm">
                                    <Checkbox
                                        id="confirm-order-send-message"
                                        checked={sendMessage}
                                        onCheckedChange={(checked) => setSendMessage(checked === true)}
                                        className="mt-0.5"
                                    />
                                    <span className="space-y-0.5">
                                        <span className="block font-medium text-foreground">{t('confirmDialog.sendMessage')}</span>
                                        <span className="block text-xs text-muted-foreground">
                                            {t('confirmDialog.sendMessageHint')}
                                        </span>
                                    </span>
                                </label>

                                <Textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    rows={4}
                                    disabled={!sendMessage}
                                    placeholder={t('confirmDialog.messagePlaceholder')}
                                    aria-label={t('confirmDialog.sendMessage')}
                                />
                            </div>
                        </>
                    ) : order?.channel && order.channel !== 'WHATSAPP' ? (
                        // Not a warning: this is how a web order is SUPPOSED to work (§7).
                        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground">
                            <Info className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{t('confirmDialog.trackingIsStatusSurface')}</span>
                        </div>
                    ) : (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <span>{t('confirmDialog.noPhone')}</span>
                        </div>
                    )}

                    {error && (
                        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-300">{error}</div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onCancel} disabled={loading}>
                        {t('confirmDialog.cancel')}
                    </Button>
                    <Button onClick={handleConfirm} disabled={loading}>
                        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {willSend ? t('confirmDialog.confirmAndSend') : t('confirmDialog.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
